import { type Member } from '@/features/iam/members/Member'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { action, computed, observable } from 'mobx'
import { EMPTY, type Observable, Subject, firstValueFrom } from 'rxjs'
import {
  catchError,
  distinctUntilChanged,
  filter,
  switchMap,
  takeUntil,
} from 'rxjs/operators'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { fromStream, toStream } from '@/utils/mobx-util'
import {
  fromAbortSignal,
  shareLatest,
  whileActive,
  withAbortSignal,
} from '@/utils/rx-util'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type DraftStateSlice, type PrepareContext } from './DraftJobViewModel'
import { type StepFactory, type StepViewModel } from './OutlineViewModel'
import type { GetWorkspaceMembersFn, SearchMembersFn } from '@/features/iam'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import {
  type RequestedProviderChanged,
  RequestedProviderSelection,
} from './DraftPersister'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'

const TITLE = 'Assign a provider to the job'
const DESCRIPTION = "The Pro won't have access to the job until it's submitted."

/**
 * Request Provider Selector view model, for requesting the job's champion provider.
 */
export class RequestProviderSelectorViewModel
  implements ActivationLifecycle, DraftStateSlice
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Whether `prepare` has completed since activation.
   */
  @observable private accessor _isReady = false

  private readonly _isReady$ = toStream(() => this._isReady, true)

  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Changes subject.
   * @private
   */
  private readonly _changesSubject = new Subject<RequestedProviderChanged>()

  /**
   * The search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Changes stream.
   */
  readonly changes$ = this._changesSubject.asObservable()

  /**
   * The requested provider.
   */
  @observable
  private accessor _requestedProvider: RequestedProviderSelection | null = null

  /**
   * Emits the requested provider on subscribe, but only after `prepare` has been run.
   */
  readonly requestedProvider$ = toStream(
    () => this.requestedProvider,
    true,
  ).pipe(distinctUntilChanged(), shareLatest(), whileActive(this._isReady$))

  /**
   * Streams as MobX Observables.
   */
  private readonly _$

  /**
   * Label for requesting the first available provider.
   */
  readonly nullSelectionPlaceholder = 'First available'

  /**
   * The scroll provider for Members.
   */
  readonly scroller = new InfiniteScrollProvider<RequestedProviderSelection>({
    limit: 20,
    fetchItems: async ({ paging, signal }) =>
      this._createAlertRetryWrapper(
        'Loading providers failed',
        signal,
      )(() => this._listMembers(paging, signal)),
  })

  /**
   * Constructor.
   *
   * @param createStep Factory for creating a Step.
   * @param selectedOwner$ Observable for the selected job owner
   * @param selectedWorkspaceMember$ Observable for the selected workspace member
   * @param _isDisabled Getter for whether the selector is disabled
   * @param _canEditDraftJobsAsAdmin Getter for whether the selector is visible
   */
  constructor(
    createStep: StepFactory,
    private readonly _selectedWorkspaceMember$: Observable<Member | null>,
    private readonly _selectedOwner$: Observable<Member | null>,
    private readonly _getWorkspaceMembers: GetWorkspaceMembersFn,
    private readonly _searchMembers: SearchMembersFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _isDisabled: () => boolean,
    private readonly _canEditDraftJobsAsAdmin: () => boolean,
  ) {
    this._$ = {
      selectedWorkspaceMember: fromStream(null, this._selectedWorkspaceMember$),
      selectedOwner: fromStream(null, this._selectedOwner$),
    }

    this._lifecycle.manage(this.requestedProvider$)

    this._lifecycle.manage(
      toStream(() => this.requestedProvider, true).pipe(
        filter((requestedProvider) => requestedProvider === null),
        switchMap(() =>
          withAbortSignal((signal) => this.scroller.fetch({ signal })).pipe(
            catchError(() => EMPTY),
          ),
        ),
        whileActive(this._isReady$),
      ),
    )

    this._lifecycle.manage(
      this.searchText.debouncedValue$.pipe(
        switchMap(() =>
          withAbortSignal((signal) => this.scroller.fetch({ signal })).pipe(
            catchError(() => EMPTY),
          ),
        ),
        whileActive(this._isReady$),
      ),
    )

    this.step = createStep({
      stepId: 'request_provider_selection',
      isCompleted: () => Boolean(this._requestedProvider),
      outlineItemDisplay: () => {
        if (
          !this.requestedProvider ||
          this.requestedProvider === RequestedProviderSelection.firstAvailable
        ) {
          return {
            title: TITLE,
            description: DESCRIPTION,
            value: this.nullSelectionPlaceholder,
            visual: {
              type: 'EmptyAvatar',
            },
          }
        } else {
          return {
            title: TITLE,
            description: DESCRIPTION,
            value: this.requestedProvider.displayName,
            visual: {
              type: 'Image',
              url: this.requestedProvider.avatar,
            },
          }
        }
      },
    })
  }

  /**
   * This is the only step accessor.
   */
  @computed
  get stepAccessors() {
    const member = this._$.selectedWorkspaceMember()

    if (
      member?.permissions.adminUserList &&
      member?.permissions.adminJobCreate &&
      this._canEditDraftJobsAsAdmin()
    ) {
      return [this]
    }

    return []
  }

  /**
   * Whether the request provider selector is disabled.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * The provider the user chose, even while it isn't among the available
   * members.
   */
  @computed
  get chosenProvider() {
    return this._requestedProvider
  }

  /**
   * The currently requested provider.
   */
  @computed
  get requestedProvider() {
    if (!this._requestedProvider) {
      return null
    }

    if (
      this._requestedProvider === RequestedProviderSelection.firstAvailable ||
      this.availableMembers.includes(this._requestedProvider)
    ) {
      return this._requestedProvider
    }

    return null
  }

  /**
   * Includes the null selector.
   */
  @computed
  get filteredMembers(): readonly RequestedProviderSelection[] {
    const owner = this._$.selectedOwner()
    const searchText = this.searchText.value.toLowerCase()

    const availableMembers = this.availableMembers.filter(
      (m) => m.user.id !== owner?.user.id,
    )

    if (!searchText) {
      return [RequestedProviderSelection.firstAvailable, ...availableMembers]
    }

    return availableMembers.filter((m) =>
      m.displayName.toLowerCase().includes(searchText),
    )
  }

  /**
   * The members that can be requested as the provider.
   */
  @computed
  get availableMembers() {
    const member = this._$.selectedWorkspaceMember()
    const workspace = member?.workspace
    if (!workspace || !member?.permissions.adminUserList) {
      return []
    }

    return this._getWorkspaceMembers(workspace.id)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  @action.bound
  deactivate() {
    this._isReady = false
    this._lifecycle.deactivate()
  }

  /** @inheritDoc */
  @action.bound
  ready() {
    this._isReady = this._lifecycle.active
  }

  /**
   * Hydrates the Provider Selector state from the Job.
   *
   * @param context
   * @param signal
   */
  async prepare(context: PrepareContext, signal?: AbortSignal) {
    // Wait for the WS selector to have been prepared.
    await firstValueFrom(
      this._selectedWorkspaceMember$.pipe(takeUntil(fromAbortSignal(signal))),
      { defaultValue: null },
    )

    signal?.throwIfAborted()

    if (context.type === 'NewDraft') {
      this.setRequestedPro(RequestedProviderSelection.firstAvailable)
    } else {
      this.setRequestedPro(
        context.job.requestedPro ?? RequestedProviderSelection.firstAvailable,
      )
    }
  }

  /**
   * Sets the requested provider.
   *
   * @param provider
   */
  @action.bound
  setRequestedPro(provider: RequestedProviderSelection | null) {
    this._requestedProvider = provider
  }

  /**
   * Requests the specified provider.
   *
   * @param provider
   */
  @action.bound
  selectProvider(provider: RequestedProviderSelection | undefined | null) {
    provider ??= null

    if (provider !== this._requestedProvider) {
      this.setRequestedPro(provider)

      this._changesSubject.next({
        requestedProvider:
          provider ?? RequestedProviderSelection.firstAvailable,
        type: 'RequestedProviderChanged',
      })
    }
  }

  /**
   * Triggers new member search.
   *
   * @param newText
   */
  @action.bound
  triggerSearch(newText: string) {
    this.searchText.change(newText)
  }

  /**
   * Lists the workspace's members.
   *
   * @private
   */
  private async _listMembers(paging: CursorPageQuery, signal?: AbortSignal) {
    const member = this._$.selectedWorkspaceMember()

    if (!member?.permissions.adminUserList) {
      return CursorPaged.empty<Member>()
    }

    return this._searchMembers(
      {
        ...paging,
        workspaceId: member.workspace.id,
        searchText: this.searchText.value,
      },
      signal,
    )
  }
}

/**
 * Factory for creating a {@link RequestProviderSelectorViewModel}.
 */
export type RequestProviderSelectorViewModelFactory = ReturnType<
  typeof createRequestProviderSelectorViewModelFactory
>

/** @see RequestProviderSelectorViewModelFactory */
export function createRequestProviderSelectorViewModelFactory(
  getWorkspaceMembers: GetWorkspaceMembersFn,
  searchMembers: SearchMembersFn,
  createAlertRetryWrapper: AlertRetryWrapperFactory,
) {
  return (
    createStep: StepFactory,
    selectedWorkspaceMember$: Observable<Member | null>,
    selectedOwner$: Observable<Member | null>,
    isDisabled: () => boolean,
    canEditDraftJobsAsAdmin: () => boolean,
  ) =>
    new RequestProviderSelectorViewModel(
      createStep,
      selectedWorkspaceMember$,
      selectedOwner$,
      getWorkspaceMembers,
      searchMembers,
      createAlertRetryWrapper,
      isDisabled,
      canEditDraftJobsAsAdmin,
    )
}
